import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Modal, Stack, Typography, useMediaQuery } from "@mui/material";
import { Cameraswitch, Close } from "@mui/icons-material";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const MAX_RECORDING_SECONDS = 180;
const VIDEO_BITS_PER_SECOND = 2500000;
// Se prefiere MP4 porque se reproduce en más dispositivos que WebM.
const RECORDER_TYPES = ["video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm"];

const TEXT = {
  es: {
    takePhoto: "Tomar foto",
    startRecording: "Grabar",
    stopRecording: "Detener",
    switchCamera: "Cambiar cámara",
    close: "Cerrar",
    permissionDenied: "No hay permiso para usar la cámara. Actívalo en la configuración del navegador.",
    noCamera: "No se encontró ninguna cámara en este dispositivo.",
    cameraBusy: "No se pudo iniciar la cámara. Cierra otras apps o pestañas que la usen (Zoom, Teams, Meet) y revisa que esté permitida en Configuración > Privacidad y seguridad > Cámara.",
    retry: "Reintentar",
    noSound: "Se grabará sin sonido: no se pudo usar el micrófono.",
    cameraUnavailable: "No se pudo abrir la cámara en este navegador.",
    useSystemCamera: "Usar la cámara del sistema",
    captureError: "No se pudo guardar la captura. Intenta de nuevo.",
  },
  en: {
    takePhoto: "Take photo",
    startRecording: "Record",
    stopRecording: "Stop",
    switchCamera: "Switch camera",
    close: "Close",
    permissionDenied: "Camera permission is missing. Enable it in your browser settings.",
    noCamera: "No camera was found on this device.",
    cameraBusy: "The camera could not start. Close other apps or tabs using it (Zoom, Teams, Meet) and check that it is allowed in your device's privacy settings.",
    retry: "Try again",
    noSound: "This will record without sound: the microphone could not be used.",
    cameraUnavailable: "The camera could not be opened in this browser.",
    useSystemCamera: "Use the system camera",
    captureError: "The capture could not be saved. Please try again.",
  },
};

const errorKeyFor = (error) => {
  if (error?.name === "NotAllowedError" || error?.name === "SecurityError") return "permissionDenied";
  if (error?.name === "NotFoundError" || error?.name === "OverconstrainedError") return "noCamera";
  if (error?.name === "NotReadableError") return "cameraBusy";
  return "cameraUnavailable";
};

// Por el tipo de pantalla y no por el ancho: una ventana angosta de escritorio sigue siendo escritorio.
export const useShowCameraOptions = () => useMediaQuery("(hover: none), (pointer: coarse)", { noSsr: true });

const stopStream = (stream) => stream?.getTracks().forEach((track) => track.stop());
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const OPEN_DELAY_MS = 60;
const RETRY_DELAY_MS = 500;
// Fallos que pueden resolverse pidiendo menos: el permiso denegado no entra acá.
const RETRYABLE_ERRORS = ["NotReadableError", "AbortError", "OverconstrainedError", "NotFoundError"];

const formatElapsed = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

// mode: "photo" o "video". onCapture recibe un File, igual que un archivo de la galería.
const CameraCapture = ({ mode, onCapture, onClose, language = "es" }) => {
  const t = useTranslatedDictionary(TEXT, language);
  const isVideoMode = mode === "video";
  const [facingMode, setFacingMode] = useState("environment");
  const [canSwitch, setCanSwitch] = useState(false);
  const [ready, setReady] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [noSound, setNoSound] = useState(false);
  const [openAttempt, setOpenAttempt] = useState(0);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const discardRecordingRef = useRef(false);
  const systemInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setErrorKey("");
    setNoSound(false);

    const open = async () => {
      // React monta dos veces en desarrollo: abrir la cámara dos veces seguidas puede fallar en Windows.
      await wait(OPEN_DELAY_MS);
      if (cancelled) return;
      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorKey("cameraUnavailable");
        return;
      }
      const ideal = { facingMode: { ideal: facingMode }, width: { ideal: 1920 }, height: { ideal: 1080 } };
      const attempts = isVideoMode
        ? [{ video: ideal, audio: true }, { video: true, audio: true }, { video: ideal, audio: false }, { video: true, audio: false }]
        : [{ video: ideal, audio: false }, { video: true, audio: false }];
      try {
        let stream = null;
        let lastError = null;
        for (const constraints of attempts) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
            setNoSound(isVideoMode && !constraints.audio);
            break;
          } catch (error) {
            lastError = error;
            if (!RETRYABLE_ERRORS.includes(error?.name)) throw error;
            if (cancelled) return;
            await wait(RETRY_DELAY_MS);
            if (cancelled) return;
          }
        }
        if (!stream) throw lastError;
        if (cancelled) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) setCanSwitch(devices.filter((device) => device.kind === "videoinput").length > 1);
      } catch (error) {
        if (!cancelled) setErrorKey(errorKeyFor(error));
      }
    };
    open();

    return () => {
      cancelled = true;
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, [facingMode, isVideoMode, openAttempt]);

  // Si se cierra mientras graba, se detiene sin entregar el video.
  useEffect(() => () => {
    discardRecordingRef.current = true;
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }, []);

  const finishRecording = useCallback(() => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  }, []);

  useEffect(() => {
    if (!recording) return undefined;
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000);
      setElapsed(seconds);
      if (seconds >= MAX_RECORDING_SECONDS) finishRecording();
    }, 250);
    return () => clearInterval(interval);
  }, [recording, finishRecording]);

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) {
        setErrorKey("captureError");
        return;
      }
      onCapture(new File([blob], `foto_${Date.now()}.jpg`, { type: "image/jpeg", lastModified: Date.now() }));
    }, "image/jpeg", 0.92);
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream || typeof MediaRecorder === "undefined") {
      setErrorKey("cameraUnavailable");
      return;
    }
    const mimeType = RECORDER_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
    try {
      const recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      });
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        setRecording(false);
        if (discardRecordingRef.current) return;
        const type = (recorder.mimeType || mimeType || "video/webm").split(";")[0];
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) {
          setErrorKey("captureError");
          return;
        }
        const extension = type === "video/mp4" ? "mp4" : "webm";
        onCapture(new File([blob], `video_${Date.now()}.${extension}`, { type, lastModified: Date.now() }));
      };
      recorderRef.current = recorder;
      recorder.start(1000);
      setElapsed(0);
      setRecording(true);
    } catch {
      setErrorKey("cameraUnavailable");
    }
  };

  const handleSystemFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onCapture(file);
  };

  const hasError = Boolean(errorKey);

  return (
    <Modal open onClose={recording ? undefined : onClose} hideBackdrop>
      <Box sx={{ position: "fixed", inset: 0, bgcolor: "#000", color: "#fff", display: "flex", flexDirection: "column", outline: "none" }}>
        <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onLoadedMetadata={() => setReady(true)}
            sx={{
              width: "100%",
              height: "100%",
              display: hasError ? "none" : "block",
              objectFit: "contain",
              transform: facingMode === "user" ? "scaleX(-1)" : "none",
            }}
          />

          {hasError && (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: "100%", px: 3, textAlign: "center" }}>
              <Typography>{t(errorKey)}</Typography>
              <Button
                variant="contained"
                onClick={() => setOpenAttempt((value) => value + 1)}
                sx={{ bgcolor: "#00d4ff", color: "#041015", textTransform: "none", fontWeight: 700, borderRadius: "12px" }}
              >
                {t("retry")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => systemInputRef.current?.click()}
                sx={{ color: "#00d4ff", borderColor: "#00d4ff", textTransform: "none", fontWeight: 700, borderRadius: "12px" }}
              >
                {t("useSystemCamera")}
              </Button>
            </Stack>
          )}

          <IconButton
            aria-label={t("close")}
            onClick={onClose}
            disabled={recording}
            sx={{ position: "absolute", top: 8, left: 8, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.65)" } }}
          >
            <Close />
          </IconButton>

          {canSwitch && !hasError && (
            <IconButton
              aria-label={t("switchCamera")}
              disabled={recording}
              onClick={() => setFacingMode((value) => (value === "user" ? "environment" : "user"))}
              sx={{ position: "absolute", top: 8, right: 8, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.65)" } }}
            >
              <Cameraswitch />
            </IconButton>
          )}

          {noSound && !hasError && !recording && (
            <Typography
              variant="caption"
              sx={{ position: "absolute", left: 12, right: 12, bottom: 12, p: 1, borderRadius: "10px", textAlign: "center", bgcolor: "rgba(0,0,0,0.65)" }}
            >
              {t("noSound")}
            </Typography>
          )}

          {recording && (
            <Typography
              sx={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", px: 1.25, borderRadius: "999px", bgcolor: "#d32f2f", fontWeight: 700 }}
            >
              {formatElapsed(elapsed)} / {formatElapsed(MAX_RECORDING_SECONDS)}
            </Typography>
          )}
        </Box>

        {!hasError && (
          <Stack alignItems="center" sx={{ flexShrink: 0, py: 2 }}>
            <Box
              component="button"
              type="button"
              aria-label={isVideoMode ? t(recording ? "stopRecording" : "startRecording") : t("takePhoto")}
              disabled={!ready}
              onClick={() => {
                if (!isVideoMode) takePhoto();
                else if (recording) finishRecording();
                else startRecording();
              }}
              sx={{
                width: 72,
                height: 72,
                p: 0,
                borderRadius: "50%",
                border: "4px solid #fff",
                bgcolor: "transparent",
                display: "grid",
                placeItems: "center",
                cursor: ready ? "pointer" : "default",
                opacity: ready ? 1 : 0.4,
              }}
            >
              <Box
                sx={{
                  width: recording ? 28 : 56,
                  height: recording ? 28 : 56,
                  borderRadius: recording ? "6px" : "50%",
                  bgcolor: isVideoMode ? "#f44336" : "#fff",
                  transition: "all 0.15s ease",
                }}
              />
            </Box>
          </Stack>
        )}

        {/* Último recurso para navegadores o apps donde la cámara en vivo no está disponible */}
        <input
          ref={systemInputRef}
          type="file"
          hidden
          accept={isVideoMode ? "video/*" : "image/*"}
          capture="environment"
          onChange={handleSystemFile}
        />
      </Box>
    </Modal>
  );
};

export default CameraCapture;
